import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fabric } from 'fabric';
import warningIcon from '../../../assets/warning-icon.svg';
import { addIconToObject, IconsTypes } from '../../../components/card-editor/utils/add-icon-to-object';
import { removeEditableAreaButtons } from '../../../components/card-editor/utils/remove-editable-area-buttons';
import { useAnalyticsContext } from '../../../context/analytics-context';
import { setIsImageEditDrawerOpen, useAppContext } from '../../../context/app-context';
import { setSelectedPhotoZone, useCardContext } from '../../../context/card-context';
import { useActiveCanvas } from '../../../hooks/use-active-canvas';
import { useDatadog } from '../../../hooks/use-datadog';
import { useFeatureFlags } from '../../../hooks/use-feature-flags';
import { setPhotoZoneScaleRatio, hideMiddleControls, hideAllControls } from '../../../utils/canvas';
import { addImageToCanvas } from '../../../utils/canvas-utils/add-image-to-canvas';
import { CanvasDataTypes } from '../../../utils/canvas-utils/canvas-data-types';
import { getCardFaceClipPath } from '../../../utils/canvas-utils/get-card-face-clip-path';
import { createPhotoFromImage, setImageOnSelect } from '../utils/add-photo-utils';
import { addPhotozoneImageMoveConstraint } from '../utils/add-photozone-image-move-constraint';
import { getNextCoverPhotoZone } from '../utils/get-next-cover-photozone';
import type { FillSelectedPhotoZoneParams } from './use-photo-types';
import { useAddLowResIcon } from '../../../hooks/use-add-low-res-icon';

/**
 * Custom hook to manage photo-related functionalities in the card editor.
 * It provides methods to scale photos, select photos, and add photos to the canvas.
 */
export const usePhoto = () => {
  const { appDispatch } = useAppContext();
  const datadog = useDatadog();
  const { t } = useTranslation();
  const { firstElementSelected, updateEditFormats, isReplacingImage } = useAnalyticsContext();
  const { cardState, cardDispatch } = useCardContext();
  const { selectedPhotoZone, cardFacesList, activeCardIndex } = cardState;
  const canvas = useActiveCanvas();
  const currentCanvas = canvas?.current;
  const currentCardFace = cardFacesList[`${activeCardIndex}`];
  const canvasObjects = currentCanvas?.getObjects() ?? [];
  const { IS_FORCE_FILL_ENABLED } = useFeatureFlags();
  const handleIconAdded = useAddLowResIcon();

  /**
   * Fills a given photo-zone using the id. This will update the canvas with the given image
   * @param params Object containing image, photozoneId, and onIconAdded callback
   */
  const fillPhotoZone = ({ image, photozoneId, onIconAdded }: FillSelectedPhotoZoneParams): void => {
    const currentPhotozoneObject = canvasObjects.find(
      (object) => object.name === (photozoneId ?? selectedPhotoZone?.id),
    );
    const shouldHideAllHandles = currentCardFace.type === 'front' && currentCardFace.active === true;
    if (currentPhotozoneObject) {
      const clipPath = new fabric.Rect({
        width: currentPhotozoneObject.width,
        height: currentPhotozoneObject.height,
        left: currentPhotozoneObject.left,
        angle: currentPhotozoneObject.angle,
        top: currentPhotozoneObject.top,
        absolutePositioned: true,
      });
      const imageDimensions = {
        left: currentPhotozoneObject.left ?? 0,
        top: currentPhotozoneObject.top ?? 0,
      };
      if (image.data.positionOverrides) {
        imageDimensions.left = image.data.positionOverrides.left;
        imageDimensions.top = image.data.positionOverrides.top;
      }
      const coverScale = setPhotoZoneScaleRatio(
        image.width as number,
        image.height as number,
        currentPhotozoneObject.width as number,
        currentPhotozoneObject.height as number,
      );
      image.set({
        ...imageDimensions,
        clipPath,
        data: {
          ...image.data,
          photoZoneId: currentPhotozoneObject?.name,
          type: CanvasDataTypes.PhotoZoneImage,
        },
        angle: currentPhotozoneObject.angle,
        scaleX: coverScale,
        scaleY: coverScale,
        onSelect: () => {
          image.set({
            data: {
              ...image.data,
            },
          });
          return false;
        },
      });
      hideMiddleControls(image);
      if (IS_FORCE_FILL_ENABLED && shouldHideAllHandles) {
        addPhotozoneImageMoveConstraint(image, currentPhotozoneObject);
        hideAllControls(image);
      } else {
        hideMiddleControls(image);
      }
      currentCanvas?.add(image);
      image.sendToBack();
      if (currentCanvas) {
        addIconToObject(currentCanvas as fabric.Canvas, image, warningIcon, IconsTypes.LowRes, {
          iconOffset: 35,
          isLowResIcon: true,
          onIconAdded,
          isStaticIcon: true,
          photoZone: currentPhotozoneObject,
        });
      }
      currentPhotozoneObject.set({ visible: false, data: { ...currentPhotozoneObject.data } });
      currentPhotozoneObject.data.hasContent = true;
      canvasObjects.forEach((object) => {
        if (object.name === `${currentPhotozoneObject?.name}-button`) {
          object.set({ visible: false });
        }
        if (object.name === CanvasDataTypes.PhotoFrameName) {
          object.bringToFront();
        }
      });
      currentCanvas?.setActiveObject(image);
    }
  };

  /**
   * Add a photo to a photo zone (front face logic)
   * @param {fabric.Image} image - The image to be added to the photo zone
   * @returns {void}
   */
  const addPhotoToPhotozone = useCallback(
    (image: fabric.Image) => {
      setImageOnSelect(image, appDispatch, isReplacingImage, updateEditFormats, firstElementSelected);
      if (image?.width === 0 && image?.height === 0) {
        throw new Error('The height and width of the image being attempted to upload is 0 (zero).');
      }

      const photo = createPhotoFromImage(image);

      datadog.addAction('add-photo-to-user-zone', {
        image: photo,
        zoneId: selectedPhotoZone.id,
        cardFace: currentCardFace?.type ?? null,
      });
      if (selectedPhotoZone.id) {
        fillPhotoZone({ image, onIconAdded: handleIconAdded });
      } else {
        const nextCoverSlot = getNextCoverPhotoZone(canvas?.current as fabric.Canvas);
        if (nextCoverSlot) {
          fillPhotoZone({
            image,
            onIconAdded: handleIconAdded,
            photozoneId: nextCoverSlot.name,
          });
        }
      }
      setSelectedPhotoZone(cardDispatch, null, null);
      setTimeout(() => setIsImageEditDrawerOpen(appDispatch), 0);
    },
    [
      appDispatch,
      t,
      cardDispatch,
      isReplacingImage,
      updateEditFormats,
      firstElementSelected,
      canvas,
      currentCanvas,
      currentCardFace,
      canvasObjects,
      IS_FORCE_FILL_ENABLED,
      selectedPhotoZone,
    ],
  );

  /**
   * Add a photo directly to the card face (non-front face logic)
   * @param {fabric.Image} image - The image to be added to the card face
   * @returns {void}
   */
  const addPhotoToCardFace = useCallback(
    (image: fabric.Image) => {
      setImageOnSelect(image, appDispatch, isReplacingImage, updateEditFormats, firstElementSelected);
      if (image?.width === 0 && image?.height === 0) {
        throw new Error('The height and width of the image being attempted to upload is 0 (zero).');
      }

      const photo = createPhotoFromImage(image);

      datadog.addAction('add-photo-to-card-face', {
        image: photo,
        zoneId: selectedPhotoZone.id,
        cardFace: currentCardFace?.type ?? null,
      });
      const cardFaceClipPath = getCardFaceClipPath(currentCardFace, 0);
      if (cardFaceClipPath) {
        image.set({
          top: cardFaceClipPath.top + cardFaceClipPath.height / 2 - 100,
          left: cardFaceClipPath.left + cardFaceClipPath.width / 2 - 100,
          clipPath: cardFaceClipPath,
        });
      }
      addImageToCanvas(image, canvas?.current as fabric.Canvas, handleIconAdded);
      removeEditableAreaButtons(canvas?.current as fabric.Canvas);
      setSelectedPhotoZone(cardDispatch, null, null);
      setTimeout(() => setIsImageEditDrawerOpen(appDispatch), 0);
    },
    [appDispatch, t, cardDispatch, isReplacingImage, updateEditFormats, firstElementSelected, canvas],
  );

  return { addPhotoToPhotozone, addPhotoToCardFace, fillPhotoZone };
};
